
import { useState } from 'react';

export interface Plugin {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  version: string;
  author: string;
  rating: number;
  installs: string;
  free: boolean;
  price?: string;
  tags: string[];
  color: string;
}

const pluginCatalog: Plugin[] = [
  // Analytics
  { id: 'analytics-pro', name: 'Analytics Pro', description: 'Track user behavior, screen views, events, and conversion funnels with real-time dashboards.', icon: 'ri-bar-chart-2-line', category: 'Analytics', version: '2.4.1', author: 'AppWizard', rating: 4.9, installs: '120k', free: true, tags: ['tracking', 'events', 'dashboard'], color: '#14B8A6' },
  { id: 'heatmap', name: 'Heatmap Tracker', description: 'Visualize where users tap and scroll most in your app with color-coded heatmaps.', icon: 'ri-fire-line', category: 'Analytics', version: '1.2.0', author: 'DataViz Labs', rating: 4.7, installs: '45k', free: false, price: '$9/mo', tags: ['heatmap', 'ux', 'taps'], color: '#F97316' },
  { id: 'crash-reporter', name: 'Crash Reporter', description: 'Automatically detect and report app crashes with full stack traces and device info.', icon: 'ri-bug-line', category: 'Analytics', version: '3.0.2', author: 'BugHunter', rating: 4.8, installs: '88k', free: true, tags: ['crash', 'errors', 'debug'], color: '#EF4444' },

  // Monetization
  { id: 'in-app-purchase', name: 'In-App Purchases', description: 'Sell digital products, subscriptions, and premium features directly inside your app.', icon: 'ri-shopping-cart-line', category: 'Monetization', version: '4.1.0', author: 'AppWizard', rating: 4.9, installs: '200k', free: true, tags: ['payments', 'subscriptions', 'store'], color: '#10B981' },
  { id: 'ads-network', name: 'Ads Network', description: 'Monetize your app with banner, interstitial, and rewarded video ads from top ad networks.', icon: 'ri-advertisement-line', category: 'Monetization', version: '2.0.5', author: 'AdBoost', rating: 4.3, installs: '95k', free: true, tags: ['ads', 'revenue', 'banner'], color: '#F59E0B' },
  { id: 'stripe-pay', name: 'Stripe Payments', description: 'Accept credit cards, Apple Pay, and Google Pay with Stripe\'s secure payment gateway.', icon: 'ri-bank-card-line', category: 'Monetization', version: '3.2.1', author: 'Stripe', rating: 4.9, installs: '310k', free: true, tags: ['stripe', 'payments', 'cards'], color: '#6366F1' },

  // Social
  { id: 'social-login', name: 'Social Login', description: 'Let users sign in with Google, Apple, Facebook, and Twitter in one tap.', icon: 'ri-user-shared-line', category: 'Social', version: '2.1.3', author: 'AuthKit', rating: 4.8, installs: '175k', free: true, tags: ['login', 'google', 'facebook'], color: '#3B82F6' },
  { id: 'push-notifications', name: 'Push Notifications', description: 'Send targeted push notifications to re-engage users with rich media and deep links.', icon: 'ri-notification-3-line', category: 'Social', version: '5.0.0', author: 'AppWizard', rating: 4.9, installs: '250k', free: true, tags: ['push', 'notifications', 'engagement'], color: '#8B5CF6' },
  { id: 'live-chat', name: 'Live Chat', description: 'Add real-time in-app chat support with agent routing, canned replies, and chat history.', icon: 'ri-chat-3-line', category: 'Social', version: '1.8.2', author: 'ChatFlow', rating: 4.6, installs: '62k', free: false, price: '$19/mo', tags: ['chat', 'support', 'messaging'], color: '#06B6D4' },
  { id: 'reviews', name: 'Ratings & Reviews', description: 'Collect in-app star ratings and written reviews to build social proof and trust.', icon: 'ri-star-line', category: 'Social', version: '1.3.0', author: 'ReviewKit', rating: 4.7, installs: '80k', free: true, tags: ['reviews', 'ratings', 'feedback'], color: '#F59E0B' },

  // Media
  { id: 'image-editor', name: 'Image Editor', description: 'Let users crop, filter, and annotate photos directly inside your app.', icon: 'ri-image-edit-line', category: 'Media', version: '2.5.0', author: 'PixelKit', rating: 4.6, installs: '55k', free: false, price: '$12/mo', tags: ['images', 'filters', 'crop'], color: '#EC4899' },
  { id: 'video-player', name: 'Video Streaming', description: 'Stream HLS and MP4 videos with adaptive bitrate, subtitles, and picture-in-picture.', icon: 'ri-video-line', category: 'Media', version: '3.1.0', author: 'StreamPro', rating: 4.8, installs: '130k', free: false, price: '$15/mo', tags: ['video', 'streaming', 'hls'], color: '#EF4444' },
  { id: 'audio-player', name: 'Audio Player', description: 'Add a full-featured audio player with playlists, background playback, and waveform display.', icon: 'ri-music-2-line', category: 'Media', version: '1.4.1', author: 'SoundKit', rating: 4.5, installs: '38k', free: true, tags: ['audio', 'music', 'podcast'], color: '#8B5CF6' },
  { id: 'ar-camera', name: 'AR Camera', description: 'Add augmented reality filters, face effects, and object overlays using the device camera.', icon: 'ri-camera-lens-line', category: 'Media', version: '1.0.3', author: 'ARStudio', rating: 4.4, installs: '22k', free: false, price: '$29/mo', tags: ['ar', 'camera', 'filters'], color: '#14B8A6' },

  // Location
  { id: 'maps-pro', name: 'Maps Pro', description: 'Embed interactive maps with custom markers, routes, geofencing, and location search.', icon: 'ri-map-2-line', category: 'Location', version: '4.0.1', author: 'MapKit', rating: 4.8, installs: '145k', free: true, tags: ['maps', 'gps', 'geofencing'], color: '#10B981' },
  { id: 'delivery-tracker', name: 'Delivery Tracker', description: 'Real-time order and delivery tracking with live driver location on a map.', icon: 'ri-truck-line', category: 'Location', version: '2.2.0', author: 'TrackIt', rating: 4.7, installs: '70k', free: false, price: '$14/mo', tags: ['delivery', 'tracking', 'orders'], color: '#F97316' },
  { id: 'store-locator', name: 'Store Locator', description: 'Help users find your nearest physical locations with directions and store hours.', icon: 'ri-store-2-line', category: 'Location', version: '1.5.0', author: 'LocateKit', rating: 4.5, installs: '41k', free: true, tags: ['stores', 'locator', 'directions'], color: '#6366F1' },

  // Productivity
  { id: 'calendar-sync', name: 'Calendar Sync', description: 'Sync events with Google Calendar, Apple Calendar, and Outlook with two-way updates.', icon: 'ri-calendar-check-line', category: 'Productivity', version: '2.0.0', author: 'CalSync', rating: 4.6, installs: '58k', free: true, tags: ['calendar', 'events', 'sync'], color: '#3B82F6' },
  { id: 'offline-mode', name: 'Offline Mode', description: 'Cache data and allow full app functionality even without an internet connection.', icon: 'ri-wifi-off-line', category: 'Productivity', version: '1.9.0', author: 'AppWizard', rating: 4.8, installs: '110k', free: true, tags: ['offline', 'cache', 'sync'], color: '#6B7280' },
  { id: 'ai-chatbot', name: 'AI Chatbot', description: 'Embed a GPT-powered chatbot to answer user questions and guide them through your app.', icon: 'ri-robot-line', category: 'Productivity', version: '1.1.0', author: 'BotBuilder', rating: 4.9, installs: '95k', free: false, price: '$25/mo', tags: ['ai', 'chatbot', 'gpt'], color: '#14B8A6' },
  { id: 'dark-mode', name: 'Dark Mode', description: 'Automatically switch between light and dark themes based on system preferences.', icon: 'ri-moon-line', category: 'Productivity', version: '1.0.0', author: 'ThemeKit', rating: 4.7, installs: '200k', free: true, tags: ['dark mode', 'theme', 'ui'], color: '#1F2937' },
];

const pluginCategories = ['All', 'Analytics', 'Monetization', 'Social', 'Media', 'Location', 'Productivity'];

interface PluginManagerProps {
  onClose: () => void;
}

export default function PluginManager({ onClose }: PluginManagerProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [installed, setInstalled] = useState<Set<string>>(new Set(['push-notifications', 'social-login']));
  const [installing, setInstalling] = useState<string | null>(null);
  const [detailPlugin, setDetailPlugin] = useState<Plugin | null>(null);
  const [filterFree, setFilterFree] = useState(false);

  const filtered = pluginCatalog.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase()) ||
      p.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    const matchFree = !filterFree || p.free;
    return matchSearch && matchCat && matchFree;
  });

  const handleInstall = (plugin: Plugin) => {
    if (installed.has(plugin.id)) {
      const next = new Set(installed);
      next.delete(plugin.id);
      setInstalled(next);
      return;
    }
    setInstalling(plugin.id);
    setTimeout(() => {
      setInstalled((prev) => new Set([...prev, plugin.id]));
      setInstalling(null);
      showToast(`✓ ${plugin.name} installed!`);
    }, 1200);
  };

  const showToast = (msg: string) => {
    const toast = document.createElement('div');
    toast.className =
      'fixed top-6 right-6 bg-teal-500 text-white px-5 py-3 rounded-xl shadow-xl z-[300] flex items-center gap-2 text-sm font-semibold';
    toast.innerHTML = `<i class="ri-check-line text-lg"></i> ${msg}`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2500);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl">
              <i className="ri-plug-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Plugin Manager</h2>
              <p className="text-xs text-gray-500">{installed.size} installed · {pluginCatalog.length} available</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-600"></i>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Category Sidebar */}
          <div className="w-44 border-r border-gray-100 flex flex-col py-3 flex-shrink-0">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-4 mb-2">Categories</p>
            {pluginCategories.map((cat) => {
              const count = cat === 'All' ? pluginCatalog.length : pluginCatalog.filter((p) => p.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex items-center justify-between px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-teal-50 text-teal-700 border-r-2 border-teal-500'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span>{cat}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === cat ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>{count}</span>
                </button>
              );
            })}

            <div className="mt-auto px-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Installed</p>
              {pluginCatalog
                .filter((p) => installed.has(p.id))
                .map((p) => (
                  <div key={p.id} className="flex items-center gap-2 py-1.5">
                    <div className="w-5 h-5 flex items-center justify-center rounded" style={{ backgroundColor: p.color + '20' }}>
                      <i className={`${p.icon} text-xs`} style={{ color: p.color }}></i>
                    </div>
                    <span className="text-xs text-gray-700 truncate">{p.name}</span>
                  </div>
                ))}
            </div>
          </div>

          {/* Right: Plugin Grid */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search + Filter */}
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search plugins..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:border-teal-400 focus:ring-1 focus:ring-teal-400/20 outline-none bg-gray-50"
                />
              </div>
              <button
                onClick={() => setFilterFree(!filterFree)}
                className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-all whitespace-nowrap cursor-pointer ${
                  filterFree ? 'bg-teal-50 border-teal-300 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <i className="ri-gift-line mr-1"></i>Free Only
              </button>
              <span className="text-xs text-gray-400 whitespace-nowrap">{filtered.length} plugins</span>
            </div>

            {/* Plugin Cards */}
            <div className="flex-1 overflow-y-auto p-5">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-3">
                    <i className="ri-plug-line text-2xl text-gray-400"></i>
                  </div>
                  <p className="text-sm font-semibold text-gray-600">No plugins found</p>
                  <p className="text-xs text-gray-400 mt-1">Try a different search or category</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {filtered.map((plugin) => {
                    const isInstalled = installed.has(plugin.id);
                    const isInstalling = installing === plugin.id;
                    return (
                      <div
                        key={plugin.id}
                        className={`group relative bg-white border rounded-xl p-4 transition-all hover:shadow-md cursor-pointer ${
                          isInstalled ? 'border-teal-200 bg-teal-50/30' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setDetailPlugin(plugin)}
                      >
                        {isInstalled && (
                          <div className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center bg-teal-500 rounded-full">
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                        )}
                        <div className="flex items-start gap-3 mb-3">
                          <div
                            className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                            style={{ backgroundColor: plugin.color + '18' }}
                          >
                            <i className={`${plugin.icon} text-xl`} style={{ color: plugin.color }}></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-gray-900 truncate">{plugin.name}</p>
                            <p className="text-xs text-gray-500">{plugin.author} · v{plugin.version}</p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed mb-3 line-clamp-2">{plugin.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <i
                                  key={s}
                                  className={`ri-star-${s <= Math.round(plugin.rating) ? 'fill' : 'line'} text-xs`}
                                  style={{ color: s <= Math.round(plugin.rating) ? '#F59E0B' : '#D1D5DB' }}
                                ></i>
                              ))}
                            </div>
                            <span className="text-xs text-gray-400">{plugin.installs}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${plugin.free ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {plugin.free ? 'Free' : plugin.price}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleInstall(plugin); }}
                              disabled={isInstalling}
                              className={`px-3 py-1 text-xs font-bold rounded-lg transition-all whitespace-nowrap cursor-pointer ${
                                isInstalling
                                  ? 'bg-gray-100 text-gray-400 cursor-wait'
                                  : isInstalled
                                  ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                  : 'bg-teal-500 text-white hover:bg-teal-600'
                              }`}
                            >
                              {isInstalling ? (
                                <span className="flex items-center gap-1"><i className="ri-loader-4-line animate-spin"></i>Installing</span>
                              ) : isInstalled ? (
                                'Remove'
                              ) : (
                                'Install'
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Plugin Detail Modal */}
      {detailPlugin && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setDetailPlugin(null); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0"
                  style={{ backgroundColor: detailPlugin.color + '18' }}
                >
                  <i className={`${detailPlugin.icon} text-2xl`} style={{ color: detailPlugin.color }}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900">{detailPlugin.name}</h3>
                  <p className="text-sm text-gray-500">{detailPlugin.author} · v{detailPlugin.version} · {detailPlugin.category}</p>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className={`ri-star-${s <= Math.round(detailPlugin.rating) ? 'fill' : 'line'} text-sm`}
                        style={{ color: s <= Math.round(detailPlugin.rating) ? '#F59E0B' : '#D1D5DB' }}></i>
                    ))}
                    <span className="text-xs text-gray-500 ml-1">{detailPlugin.rating} · {detailPlugin.installs} installs</span>
                  </div>
                </div>
                <button onClick={() => setDetailPlugin(null)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>

              <p className="text-sm text-gray-700 leading-relaxed mb-4">{detailPlugin.description}</p>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {detailPlugin.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">#{tag}</span>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div>
                  <span className={`text-sm font-bold px-3 py-1.5 rounded-full ${detailPlugin.free ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {detailPlugin.free ? '✓ Free Plugin' : detailPlugin.price}
                  </span>
                </div>
                <button
                  onClick={() => { handleInstall(detailPlugin); setDetailPlugin(null); }}
                  className={`px-5 py-2 text-sm font-bold rounded-xl transition-all whitespace-nowrap cursor-pointer ${
                    installed.has(detailPlugin.id)
                      ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                      : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 shadow-md shadow-teal-500/20'
                  }`}
                >
                  {installed.has(detailPlugin.id) ? (
                    <><i className="ri-delete-bin-line mr-1.5"></i>Remove Plugin</>
                  ) : (
                    <><i className="ri-download-line mr-1.5"></i>Install Plugin</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
